import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FieldWrapper } from '@ngx-formly/core';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { merge, takeUntil } from 'rxjs';

/**
 * Adds a character counter to the end-hint slot (`props.hintEnd`) of any
 * Formly Material field that renders through `formly-wrapper-mat-form-field`
 * — in practice the plain `input`/`textarea` types, gated by
 * `max-length-counter.extension.ts` on `props.maxLength` being set.
 * `props.hintEnd` is an unused slot in ngx-formly/material's generic
 * wrapper: it renders as a second `<mat-hint align="end">`, next to
 * `props.description`/`hintStart`'s `<mat-hint>` — Material's own subscript
 * row already inserts a flex spacer between the two once both are present.
 *
 * Modeled on `FormlyWrapperClearButton` (`props.suffix` /
 * `<ng-template>` teleported via `ngTemplateOutlet`) — same static ViewChild
 * + ngOnInit assignment + zoneless valueChanges/statusChanges subscription.
 */
@Component({
  selector: 'formly-wrapper-max-length-counter',
  template: `
    <!-- Textareas show the counter INSIDE the field box, bottom-left (Figma);
         single-line inputs keep the hint-row slot (no room inside the box). -->
    @if (isTextarea()) {
      <div class="dd-maxlength-inside">
        <ng-container #fieldComponent></ng-container>
        <span #counter class="dd-maxlength-inside__counter"
          >{{ currentLength() }}/{{ props.maxLength }} karakters</span
        >
      </div>
    } @else {
      <ng-container #fieldComponent></ng-container>
    }

    <ng-template #matHintEnd>{{ currentLength() }}/{{ props.maxLength }} karakters</ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyWrapperMaxLengthCounter extends FieldWrapper implements OnInit, AfterViewInit {
  @ViewChild('matHintEnd', { static: true }) matHintEnd!: TemplateRef<unknown>;
  @ViewChild('counter') counter?: ElementRef<HTMLElement>;
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #destroy$ = injectDestroy();

  /**
   * Assigned in ngOnInit, not ngAfterViewInit: `formly-wrapper-mat-form-field`
   * gates the hintEnd slot behind `@if (props.hintEnd)`, and that check runs
   * in the parent's update pass — before any child's AfterViewInit.
   * `static: true` makes the template available this early.
   */
  isTextarea(): boolean {
    return this.field.type === 'textarea' || this.field.type === 'linked-value';
  }

  ngOnInit() {
    if (this.matHintEnd && !this.isTextarea()) {
      this.props.hintEnd = this.matHintEnd;
    }

    // Zoneless app: props.hintEnd's @if depends on formControl state, which
    // Formly won't re-check on its own — mirrors FormlyWrapperClearButton's
    // setupValueChanges pattern.
    merge(this.formControl.valueChanges, this.formControl.statusChanges)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(() => this.#changeDetectorRef.markForCheck());
  }

  /**
   * Moves the counter into Material's field box (`.mat-mdc-text-field-wrapper`, which already is
   * `position: relative`). Left as a sibling of the field it would anchor to the wrapper div —
   * whose height also covers the subscript row — so the counter landed BELOW the box, on top of
   * the hint text. The box is created once per field, so a single move on view-init is enough.
   */
  ngAfterViewInit() {
    const counter = this.counter?.nativeElement;
    const box = this.#elementRef.nativeElement.querySelector('.mat-mdc-text-field-wrapper');
    if (counter && box && counter.parentElement !== box) {
      box.appendChild(counter);
    }
  }

  currentLength(): number {
    const value = this.formControl?.value;
    return typeof value === 'string' ? value.length : 0;
  }
}
